import { useEffect, useState, type FormEvent } from "react";
import { AdminApi, type Moderator } from "../api/admin";
import { useAuth } from "../auth/AuthContext";

const initialForm = { email: "", password: "", first_name: "", last_name: "" };

export const CreateModeratorPage = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === "ADMIN";

  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [moderators, setModerators] = useState<Moderator[]>([]);
  const [loadingList, setLoadingList] = useState(true);

  const loadList = () => {
    setLoadingList(true);
    AdminApi.listModerators()
      .then((res) => setModerators(res.data))
      .catch(() => setModerators([]))
      .finally(() => setLoadingList(false));
  };

  useEffect(loadList, []);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    setSuccess(null);
    try {
      const res = await AdminApi.createModerator({
        email: form.email.trim(),
        password: form.password,
        first_name: form.first_name.trim() || undefined,
        last_name: form.last_name.trim() || undefined,
      });
      setSuccess(`Модератор ${res.data.email} создан (id=${res.data.id}).`);
      setForm(initialForm);
      loadList();
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      if (Array.isArray(detail)) {
        setError(detail.map((d: any) => d.msg).join(", "));
      } else if (typeof detail === "string") {
        setError(detail);
      } else {
        setError(err?.message || "Не удалось создать модератора");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const onDeactivate = async (m: Moderator) => {
    if (!confirm(`Деактивировать модератора ${m.email}?`)) return;
    try {
      await AdminApi.deactivateModerator(m.id);
      loadList();
    } catch (err: any) {
      alert(err?.response?.data?.detail || "Не удалось деактивировать");
    }
  };

  return (
    <div className="p-8 max-w-5xl">
      <h1 className="text-2xl font-semibold text-slate-900 mb-1">Модераторы</h1>
      <p className="text-sm text-slate-500 mb-6">
        Создание и управление учётными записями модераторов. Модератор получает доступ только
        к разделу верификации заявок организаторов и поставщиков.
      </p>

      {!isAdmin && (
        <div className="mb-6 p-4 border border-amber-300 bg-amber-50 text-amber-900 rounded-md text-sm">
          Создание модераторов доступно только пользователю с ролью ADMIN.
        </div>
      )}

      {isAdmin && (
        <section className="bg-white border border-slate-200 rounded-xl shadow-sm p-6 mb-8">
          <h2 className="text-base font-semibold text-slate-900 mb-4">Создать модератора</h2>
          <form onSubmit={onSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
              <input
                type="email"
                required
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-brand/40"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Пароль <span className="text-slate-500 font-normal">(не короче 8 символов)</span>
              </label>
              <input
                type="password"
                required
                minLength={8}
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-brand/40"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Имя</label>
              <input
                type="text"
                value={form.first_name}
                onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-brand/40"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Фамилия</label>
              <input
                type="text"
                value={form.last_name}
                onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-brand/40"
              />
            </div>

            {error && <div className="md:col-span-2 text-sm text-red-600">{error}</div>}
            {success && <div className="md:col-span-2 text-sm text-emerald-700">{success}</div>}

            <div className="md:col-span-2">
              <button
                type="submit"
                disabled={submitting}
                className="px-4 py-2 bg-brand text-white rounded-md text-sm font-medium hover:opacity-90 disabled:opacity-60"
              >
                {submitting ? "Создание..." : "Создать модератора"}
              </button>
            </div>
          </form>
        </section>
      )}

      <section className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <h2 className="text-base font-semibold text-slate-900">Список модераторов</h2>
          <button
            type="button"
            onClick={loadList}
            className="text-sm text-slate-500 hover:text-slate-900"
          >
            Обновить
          </button>
        </div>
        {loadingList ? (
          <div className="p-8 text-center text-slate-500">Загрузка...</div>
        ) : moderators.length === 0 ? (
          <div className="p-8 text-center text-slate-500">Модераторы пока не созданы</div>
        ) : (
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="text-left px-4 py-3 font-medium">ID</th>
                <th className="text-left px-4 py-3 font-medium">Email</th>
                <th className="text-left px-4 py-3 font-medium">Имя</th>
                <th className="text-left px-4 py-3 font-medium">Статус</th>
                <th className="text-left px-4 py-3 font-medium">Создан</th>
                {isAdmin && <th className="text-left px-4 py-3 font-medium">Действия</th>}
              </tr>
            </thead>
            <tbody>
              {moderators.map((m) => (
                <tr key={m.id} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-2.5 text-slate-500">{m.id}</td>
                  <td className="px-4 py-2.5 font-medium text-slate-900">{m.email}</td>
                  <td className="px-4 py-2.5">{[m.first_name, m.last_name].filter(Boolean).join(" ") || "—"}</td>
                  <td className="px-4 py-2.5">
                    {m.is_active ? (
                      <span className="text-emerald-700">Активен</span>
                    ) : (
                      <span className="text-slate-500">Деактивирован</span>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-slate-500">
                    {m.created_at ? new Date(m.created_at).toLocaleString() : "—"}
                  </td>
                  {isAdmin && (
                    <td className="px-4 py-2.5">
                      {m.is_active && (
                        <button
                          onClick={() => onDeactivate(m)}
                          className="px-2.5 py-1 bg-red-600 text-white rounded text-xs hover:opacity-90"
                        >
                          Деактивировать
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
};
